/** Team hooks. API data only when authenticated (errors → isError + empty). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import type { TeamMember } from "@/types";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { useAuthStore } from "@/features/auth/auth.store";
import {
  findInLists,
  patchInList,
  prependToList,
  removeFromList,
  restoreSnapshot,
  snapshotList,
} from "@/lib/optimistic-cache";
import { teamApi } from "./team.api";
import type { ApiTeamMember } from "./team.adapter";
import {
  toTeamMember,
  toTeamWriteBody,
  type AccessAction,
  type MemberAccess,
  type PermissionException,
  type PermissionExceptionInput,
  type SkillInput,
  type TeamMemberInput,
} from "./team.adapter";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };
const ARCHIVED_TEAM_PARAMS = { archived: true, limit: 100 };

/** Prefix matching every team list query (active + archived) — narrower than the whole ["team"] tree. */
const TEAM_LIST_KEY = ["team", "list"] as const;

export type DataSource = "api" | "mock";

export interface UseTeamMembersResult {
  members: TeamMember[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useTeamMembers(): UseTeamMembersResult {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.teamList(LIST_PARAMS),
    queryFn: () => teamApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (query.data)
    return {
      members: query.data.data.map(toTeamMember),
      isLoading: false,
      isError: false,
      isFetching: query.isFetching,
      source: "api",
      error: null,
      refetch: query.refetch,
    };
  if (query.isError)
    return {
      members: [],
      isLoading: false,
      isError: true,
      isFetching: false,
      source: "api",
      error: query.error,
      refetch: query.refetch,
    };
  return {
    members: [],
    isLoading: query.isLoading,
    isError: false,
    isFetching: query.isFetching,
    source: "api",
    error: null,
    refetch: query.refetch,
  };
}

/** No dedicated `/team/:id` endpoint — derive a single member from the list. */
export function useTeamMember(id: string | undefined): {
  member: TeamMember | undefined;
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.teamDetail(id ?? ""),
    queryFn: () => teamApi.get(id as string),
    enabled: authed && !!id,
    // Instant paint: seed from the team list row the user just clicked.
    // `initialDataUpdatedAt: 0` marks the seed stale so the real fetch always
    // follows and reconciles the fields the list omits.
    initialData: () => findInLists<ApiTeamMember>(qc, [queryKeys.teamList(LIST_PARAMS)], id),
    initialDataUpdatedAt: 0,
  });

  if (query.data)
    return { member: toTeamMember(query.data), isLoading: false, isError: false, source: "api" };
  if (query.isError) return { member: undefined, isLoading: false, isError: true, source: "api" };
  return { member: undefined, isLoading: query.isLoading, isError: false, source: "api" };
}

export function useMyTeamProfile(): {
  member: TeamMember | undefined;
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";
  const query = useQuery({
    queryKey: queryKeys.teamMe,
    queryFn: () => teamApi.me(),
    enabled: authed,
  });

  if (query.data)
    return {
      member: toTeamMember(query.data),
      isLoading: false,
      isError: false,
      source: "api",
      refetch: query.refetch,
    };
  if (query.isError)
    return {
      member: undefined,
      isLoading: false,
      isError: true,
      source: "api",
      refetch: query.refetch,
    };
  return {
    member: undefined,
    isLoading: query.isLoading,
    isError: false,
    source: "api",
    refetch: query.refetch,
  };
}

/** Refresh the team list (rows summary) — for create/archive/restore and profile edits. */
function useInvalidateTeamList() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: TEAM_LIST_KEY });
}

/**
 * Refresh a single member's detail tree (detail + its access/skills/documents/
 * projects sub-queries, which all hang under ["team","detail",id]). Used by the
 * per-member sub-resource mutations that never change the list row summary.
 */
function useInvalidateTeamMember() {
  const qc = useQueryClient();
  return (id: string) => void qc.invalidateQueries({ queryKey: queryKeys.teamDetail(id) });
}

export function useCreateTeamMember() {
  const qc = useQueryClient();
  const invalidateList = useInvalidateTeamList();
  return useMutation({
    mutationFn: async (input: TeamMemberInput) => teamApi.create(toTeamWriteBody(input)),
    onSuccess: (data) => {
      prependToList<ApiTeamMember>(qc, queryKeys.teamList(LIST_PARAMS), data as ApiTeamMember);
      invalidateList();
    },
  });
}

export function useUpdateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: TeamMemberInput }) =>
      teamApi.update(id, toTeamWriteBody(input)),
    onSuccess: (data, { id }) => {
      // Reflect the edited row in place, then reconcile the detail/self views.
      patchInList<ApiTeamMember>(qc, queryKeys.teamList(LIST_PARAMS), id, data as ApiTeamMember);
      void qc.invalidateQueries({ queryKey: TEAM_LIST_KEY });
      void qc.invalidateQueries({ queryKey: queryKeys.teamDetail(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.teamMe });
    },
  });
}

export function useArchiveTeamMember() {
  const qc = useQueryClient();
  const invalidateList = useInvalidateTeamList();
  return useMutation({
    mutationFn: async (id: string) => teamApi.archive(id),
    onMutate: async (id: string) => {
      const listKey = queryKeys.teamList(LIST_PARAMS);
      await qc.cancelQueries({ queryKey: listKey });
      const snapshot = snapshotList(qc, [listKey]);
      removeFromList<ApiTeamMember>(qc, listKey, id);
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      if (context?.snapshot) restoreSnapshot(qc, context.snapshot);
    },
    onSettled: () => {
      invalidateList();
    },
  });
}

export function useArchivedTeamMembers() {
  const authed = useAuthStore((s) => s.status) === "authenticated";
  const query = useQuery({
    queryKey: queryKeys.teamList(ARCHIVED_TEAM_PARAMS),
    queryFn: () => teamApi.list(ARCHIVED_TEAM_PARAMS),
    enabled: authed,
  });
  if (query.data)
    return {
      members: query.data.data.map(toTeamMember),
      isLoading: false,
      isError: false,
      refetch: query.refetch,
    };
  if (query.isError)
    return { members: [] as TeamMember[], isLoading: false, isError: true, refetch: query.refetch };
  return {
    members: [] as TeamMember[],
    isLoading: query.isLoading,
    isError: false,
    refetch: query.refetch,
  };
}

export function useRestoreTeamMember() {
  const qc = useQueryClient();
  const invalidateList = useInvalidateTeamList();
  return useMutation({
    mutationFn: async (id: string) => teamApi.restore(id),
    onMutate: async (id: string) => {
      const archivedKey = queryKeys.teamList(ARCHIVED_TEAM_PARAMS);
      await qc.cancelQueries({ queryKey: archivedKey });
      const snapshot = snapshotList(qc, [archivedKey]);
      removeFromList<ApiTeamMember>(qc, archivedKey, id);
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      if (context?.snapshot) restoreSnapshot(qc, context.snapshot);
    },
    onSettled: () => {
      invalidateList();
    },
  });
}

// ── Extended member detail: access, skills, documents, projects, invitations ──

/** GET /team/:id/access — enabled when authenticated + id present. */
export function useMemberAccess(memberId: string | undefined): {
  access: MemberAccess | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const authed = useAuthStore((s) => s.status) === "authenticated";
  const query = useQuery({
    queryKey: [...queryKeys.teamDetail(memberId ?? ""), "access"] as const,
    queryFn: () => teamApi.getAccess(memberId as string),
    enabled: authed && !!memberId,
  });
  return {
    access: query.data,
    isLoading: query.isLoading && authed && !!memberId,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useAddSkill() {
  const invalidateMember = useInvalidateTeamMember();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SkillInput }) =>
      teamApi.addSkill(id, body as unknown as Record<string, unknown>),
    onError: (err) => toast.error(getErrorMessage(err, "Échec de l'ajout de la compétence")),
    onSuccess: (_data, { id }) => invalidateMember(id),
  });
}

export function useRemoveSkill() {
  const invalidateMember = useInvalidateTeamMember();
  return useMutation({
    mutationFn: ({ id, skillId }: { id: string; skillId: string }) =>
      teamApi.removeSkill(id, skillId),
    onError: (err) => toast.error(getErrorMessage(err, "Échec de la suppression de la compétence")),
    onSuccess: (_data, { id }) => invalidateMember(id),
  });
}

export function useAddDocument() {
  const invalidateMember = useInvalidateTeamMember();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      teamApi.addDocument(id, body),
    onError: (err) => toast.error(getErrorMessage(err, "Échec de l'ajout du document")),
    onSuccess: (_data, { id }) => invalidateMember(id),
  });
}

export function useUpdateDocument() {
  const invalidateMember = useInvalidateTeamMember();
  return useMutation({
    mutationFn: ({ id, docId, body }: { id: string; docId: string; body: Record<string, unknown> }) =>
      teamApi.updateDocument(id, docId, body),
    onError: (err) => toast.error(getErrorMessage(err, "Échec de la mise à jour du document")),
    onSuccess: (_data, { id }) => invalidateMember(id),
  });
}

export function useRemoveDocument() {
  const invalidateMember = useInvalidateTeamMember();
  return useMutation({
    mutationFn: ({ id, docId }: { id: string; docId: string }) =>
      teamApi.removeDocument(id, docId),
    onError: (err) => toast.error(getErrorMessage(err, "Échec de la suppression du document")),
    onSuccess: (_data, { id }) => invalidateMember(id),
  });
}

export function useAssignProject() {
  const invalidateMember = useInvalidateTeamMember();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      teamApi.assignProject(id, body),
    onError: (err) => toast.error(getErrorMessage(err, "Échec de l'affectation au projet")),
    onSuccess: (_data, { id }) => invalidateMember(id),
  });
}

export function useUnassignProject() {
  const invalidateMember = useInvalidateTeamMember();
  return useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string }) =>
      teamApi.unassignProject(id, projectId),
    onError: (err) => toast.error(getErrorMessage(err, "Échec du retrait du projet")),
    onSuccess: (_data, { id }) => invalidateMember(id),
  });
}

export function useSendInvitation() {
  const invalidateMember = useInvalidateTeamMember();
  return useMutation({
    mutationFn: (id: string) => teamApi.sendInvitation(id),
    onError: (err) => toast.error(getErrorMessage(err, "Échec de l'envoi de l'invitation")),
    onSuccess: (_data, id) => invalidateMember(id),
  });
}

export function useAccessAction() {
  const invalidateMember = useInvalidateTeamMember();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: AccessAction }) =>
      teamApi.accessAction(id, action),
    onError: (err) => toast.error(getErrorMessage(err, "Échec de l'action")),
    onSuccess: (_data, { id }) => invalidateMember(id),
  });
}

export function useResetPassword() {
  const invalidateMember = useInvalidateTeamMember();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      teamApi.resetPassword(id, body),
    onError: (err) => toast.error(getErrorMessage(err, "Échec de la réinitialisation")),
    onSuccess: (_data, { id }) => invalidateMember(id),
  });
}

// ── Exceptions de permissions individuelles (spec RBAC v2 §3) ────────────────

const permissionExceptionsKey = (userId: string) =>
  ["users", userId, "permission-exceptions"] as const;

/** GET /users/:userId/permission-exceptions — requiert users.permission-exceptions. */
export function usePermissionExceptions(
  userId: string | undefined,
  enabled = true,
): {
  exceptions: PermissionException[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const authed = useAuthStore((s) => s.status) === "authenticated";
  const active = authed && !!userId && enabled;
  const query = useQuery({
    queryKey: permissionExceptionsKey(userId ?? ""),
    queryFn: () => teamApi.listPermissionExceptions(userId as string),
    enabled: active,
  });
  return {
    exceptions: query.data ?? [],
    isLoading: query.isLoading && active,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** PUT — la liste envoyée remplace l'existante (ajout ET suppression). */
export function useReplacePermissionExceptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, exceptions }: { userId: string; exceptions: PermissionExceptionInput[] }) =>
      teamApi.replacePermissionExceptions(userId, exceptions),
    onSuccess: (data, { userId }) => {
      qc.setQueryData(permissionExceptionsKey(userId), data);
      void qc.invalidateQueries({ queryKey: permissionExceptionsKey(userId) });
    },
  });
}
